<template>
  <div v-if="locked" class="limits-badge" :title="title">
    <lucide-icon name="lock" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import LucideIcon from './LucideIcon.vue';
import { useLicense } from "@/api/license.store";
import { useModels } from "@/api/models.store";
import { useUser } from "@/api/user.store";

const props = defineProps<{
  type: "maxModels" | "canShare" | "canDownload" | "canPublish"
}>();

const license = useLicense();
const models = useModels();
const { user } = useUser();
const { t } = useI18n();

const locked = computed(() => {
  if (props.type == "canShare") {
    return license.limits.canShare === false;
  }
  if (props.type == "canDownload") {
    return license.limits.canDownload === false;
  }
  if (props.type == "canPublish") {
    return license.limits.canPublish === false;
  }
  if (props.type == "maxModels") {
    if (license.limits.maxModels == null) return false;
    if (user.value?.workspace == null) return false;
    const ownModels = models.byWorkspace.value[user.value.workspace.id] ?? [];
    return ownModels.length >= license.limits.maxModels;
  }
  return false;
});

const title = computed(() => {
  if (props.type == "maxModels") {
    return t("limits.maxModels", { count: license.limits.maxModels });
  }
  if (props.type == "canPublish") {
    return t("limits.publishingUpgradeRequired");
  }
  return t("limits.subscriptionRequired");
});
</script>

<style>
.limits-badge {
  position: absolute;
  top: calc(50% - 1.1em);
  right: 0;
  width: 2em;
  height: 2em;
  overflow: hidden;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: var(--color-background-close);
  border-radius: 50rem;
  border: 1px solid var(--color-grey-darker);
  color: var(--color-text-moderate);
  box-shadow: 0 2px 3px 0 var(--color-shadow-2);
}
</style>